/* ==========================================================================
   Raptor Racing motion layer, theme v6.8 (24 to 25 Sept 2026)
   Loaded with defer from layout/theme.liquid, after assets/rr-brand.css.

   Progressive enhancement only. Every element this file touches is fully
   visible and usable when it never runs: the classes that start an animation
   (rr-anim, is-in, rr-hero-in, rr-rise, rr-bump, rr-stuck) are added here,
   never in the markup. Honours prefers-reduced-motion.

   (a) heading divider draws in once when its heading enters the viewport
   (b) hero entrance on the first slideshow slide
   (c) cart count badge pops when its number changes
   (d) "Added" confirmation on the add-to-cart button, driven by the
       gv:cart:added event dispatched from assets/z__jsAjaxCart.js
   (e) sticky header: --rr-announcement-h on <html> and rr-stuck on the
       header section, so the logo clears the stuck announcement bar and
       shrinks once the page is scrolled (CSS in rr-brand.css)
   (f) phones: the chat button tucks away while the shopper scrolls down
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function reduced() {
    return !!(motionQuery && motionQuery.matches);
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  function closest(el, selector) {
    if (!el) return null;
    if (el.closest) return el.closest(selector);
    while (el && el.nodeType === 1) {
      if (el.matches && el.matches(selector)) return el;
      el = el.parentElement;
    }
    return null;
  }

  function isVisible(el) {
    return !!(el && el.getClientRects && el.getClientRects().length);
  }

  function ready(fn) {
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* ------------------------------------------------------------------------
     (a) Heading divider draw-in
     rr-anim goes on the .heading-wrapper that holds a .heading-divider; is-in
     lands when the wrapper enters the viewport. CSS in rr-brand.css 5.1.
     ------------------------------------------------------------------------ */
  var observer = null;

  function observe(el) {
    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
    }
    observer.observe(el);
  }

  function initDividers(root) {
    if (reduced() || !('IntersectionObserver' in window)) return;
    root = root || doc;

    /* 29 Sept audit: .rr-sech__bar (the brand-layer headings) draws the same
       way; its wrapper is its parent */
    each(root.querySelectorAll('.heading-divider, .rr-sech__bar'), function (divider) {
      var wrap = closest(divider, '.heading-wrapper, .heading-section__content') || (divider.classList.contains('rr-sech__bar') ? divider.parentNode : null);
      if (!wrap || wrap.classList.contains('rr-anim')) return;
      wrap.classList.add('rr-anim');
      /* Commit the scaleX(0) start state before is-in can land, so the
         transition always plays even for dividers already on screen. */
      void wrap.offsetWidth;
    });

    each(root.querySelectorAll('.rr-anim:not(.is-in)'), function (el) {
      if (el.getAttribute('data-rr-observed')) return;
      el.setAttribute('data-rr-observed', '1');
      observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     (b) Hero entrance
     Marks the first slide's pre-heading, heading, sub-heading and buttons
     (plus the section's own .slideshow__heading when present) with rr-rise in
     document order, staggers them 60ms apart, then adds rr-hero-in.
     Only elements that are actually rendered at this viewport are marked, so
     the desktop and mobile copies of the slide text do not double the stagger.
     ------------------------------------------------------------------------ */
  var HERO_PARTS = '.slideshow-slide__preheading, .slideshow-slide__heading, .slideshow-slide__subheading, .slideshow-slide__button';

  function initHero() {
    if (reduced()) return;

    var wrapper = doc.querySelector('.slideshow__wrapper, [data-slideshow-wrapper]');
    if (!wrapper || wrapper.classList.contains('rr-hero-in')) return;

    var host = closest(wrapper, 'section-slideshow, .slideshow') || wrapper.parentElement || wrapper;
    var firstSlide = wrapper.querySelector('.slideshow-slide') || wrapper;
    var parts = [];

    var sectionHeading = host.querySelector('.slideshow__heading');
    if (sectionHeading && isVisible(sectionHeading)) parts.push(sectionHeading);

    each(firstSlide.querySelectorAll(HERO_PARTS), function (el) {
      if (isVisible(el)) parts.push(el);
    });

    if (!parts.length) return;

    parts.forEach(function (el, index) {
      el.classList.add('rr-rise');
      el.style.animationDelay = (index * 60) + 'ms';
    });

    wrapper.classList.add('rr-hero-in');
    if (sectionHeading) host.classList.add('rr-hero-in');
  }

  /* ------------------------------------------------------------------------
     (c) Cart count pop
     One MutationObserver per [data-bind="itemCount"]; rr-bump is added when
     the text changes and removed on animationend. CSS in rr-brand.css 5.3.
     ------------------------------------------------------------------------ */
  /* ------------------------------------------------------------------------
     (c0) Cart count = products only (30 Sept 2026)
     Shopify's cart.item_count includes Shipping Protection and the Oversized
     Handling Fee lines, so the badge read 2 over a cart page that says 1 item.
     snippets/rr-cart-count.liquid renders the right number; Flex's cart scripts
     (z__jsCart, z__jsAjaxCart) then write the raw item_count after changes, so
     each badge write triggers one /cart.js read and a correction.
     ------------------------------------------------------------------------ */
  function isFeeLine(i) {
    return i && (i.product_type === 'Fees' || /shipping protection/i.test(i.product_type || '') ||
      /shipping-protection/.test(i.handle || '') || i.handle === 'oversize-fee');
  }
  function productCount(cart) {
    var n = 0;
    each((cart && cart.items) || [], function (i) { if (!isFeeLine(i)) n += Number(i.quantity) || 0; });
    return n;
  }
  window.rrProductCount = productCount;
  var countWanted = null;
  var countTimer = null;
  function setCount(n) {
    countWanted = String(n);
    each(doc.querySelectorAll('[data-bind="itemCount"]'), function (el) {
      if ((el.textContent || '').trim() !== countWanted) el.textContent = countWanted;
    });
  }
  function correctCount() {
    window.clearTimeout(countTimer);
    countTimer = window.setTimeout(function () {
      window.fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart) setCount(productCount(cart)); })
        .catch(function () { /* the Liquid count is right on the next page */ });
    }, 150);
  }
  function initCountFix(root) {
    if (!('MutationObserver' in window)) return;
    each((root || doc).querySelectorAll('[data-bind="itemCount"]'), function (badge) {
      if (badge.getAttribute('data-rr-count')) return;
      badge.setAttribute('data-rr-count', '1');
      new MutationObserver(function () {
        if ((badge.textContent || '').trim() !== countWanted) correctCount();
      }).observe(badge, { childList: true, characterData: true, subtree: true });
    });
  }

  /* ------------------------------------------------------------------------
     (d2) Slideshow progress line (30 Sept 2026)
     A Raptor Green line along the bottom of each autoplaying slideshow fills
     over the section's autoplay interval and restarts on every slide change
     (Swiper's slideChangeTransitionStart). Hover pause and "shopper took
     control" come from the theme's own data-hover / data-autoplay-active
     attributes (CSS in rr-brand.css 7i).
     ------------------------------------------------------------------------ */
  function initSlideProgress(root) {
    if (reduced()) return;
    each((root || doc).querySelectorAll('section-slideshow'), function (el) {
      if (el.__rrProgress || el.getAttribute('enable-autoplay') !== 'true') return;
      el.__rrProgress = true;
      var secs = parseInt(el.getAttribute('autoplay-interval'), 10) || 8;
      var host = el.querySelector('.swiper') || el;
      var bar = doc.createElement('div');
      bar.className = 'rr-slide-progress';
      bar.setAttribute('aria-hidden', 'true');
      bar.appendChild(doc.createElement('i'));
      bar.style.setProperty('--rr-slide-dur', secs + 's');
      host.appendChild(bar);
      var restart = function () {
        bar.classList.remove('is-run');
        void bar.offsetWidth;
        bar.classList.add('is-run');
      };
      var hook = function () {
        if (!el.swiper || typeof el.swiper.on !== 'function') return false;
        el.swiper.on('slideChangeTransitionStart', restart);
        restart();
        return true;
      };
      if (!hook()) {
        var tries = 0;
        var t = window.setInterval(function () { if (hook() || ++tries > 40) window.clearInterval(t); }, 250);
      }
    });
  }

  function initCartBump(root) {
    if (reduced() || !('MutationObserver' in window)) return;
    root = root || doc;

    each(root.querySelectorAll('[data-bind="itemCount"]'), function (badge) {
      if (badge.getAttribute('data-rr-bump')) return;
      badge.setAttribute('data-rr-bump', '1');

      var last = (badge.textContent || '').trim();

      var mo = new MutationObserver(function () {
        var now = (badge.textContent || '').trim();
        if (now === last) return;
        last = now;
        badge.classList.remove('rr-bump');
        void badge.offsetWidth; /* restart the animation when adds come in quick succession */
        badge.classList.add('rr-bump');
      });

      mo.observe(badge, { childList: true, characterData: true, subtree: true });

      badge.addEventListener('animationend', function (event) {
        if (event.animationName === 'rr-pop') badge.classList.remove('rr-bump');
      });
    });
  }

  /* ------------------------------------------------------------------------
     (d) Add-to-cart confirmation
     z__jsAjaxCart.js dispatches gv:cart:added with detail.button on success.
     The button's label becomes "Added" plus a check glyph for 1.6 s, then the
     original label (data-label, else the text we replaced) is restored. The
     label swap is not motion, so it also runs under reduced motion.
     ------------------------------------------------------------------------ */
  var ADDED_LABEL = 'Added';
  var ADDED_DURATION = 1600;
  var liveRegion = null;

  function announce(text) {
    if (!liveRegion) {
      liveRegion = doc.createElement('span');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      doc.body.appendChild(liveRegion);
    }
    liveRegion.textContent = '';
    window.setTimeout(function () { liveRegion.textContent = text; }, 50);
  }

  function labelOf(button) {
    return button.querySelector('.text') || button.querySelector('span:not(.icon):not(.checkmark):not(.rr-shoot):not(.rr-done)');
  }

  /* v2.16 (spec 5.2/5.3, reference button-animations-v2.html mobile 3): the
     label stays in place and flips up out of view while span.rr-done
     ("✓ Added" with a self-drawing tick) flips in from below; .rr-ok drives
     it in rr-brand.css. The theme's own label swap is left untouched so its
     text is back when .rr-ok comes off. Under reduced motion the swap is
     instant (the transitions are off) but still shown. */
  var TICK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7"/></svg>';

  function restoreLabel(button) {
    button.classList.remove('rr-ok');
    button.classList.remove('rr-added');
    button.rrAddedTimer = null;
  }

  function onCartAdded(event) {
    var button = event && event.detail && event.detail.button;
    if (!button || !button.querySelector) return;

    if (button.rrAddedTimer) window.clearTimeout(button.rrAddedTimer);

    var done = button.querySelector('.rr-done');
    if (!done) {
      done = doc.createElement('span');
      done.className = 'rr-done';
      done.setAttribute('aria-hidden', 'true');
      done.innerHTML = TICK_SVG;
      done.appendChild(doc.createTextNode(button.getAttribute('data-rr-added-label') || ADDED_LABEL));
      button.appendChild(done);
    }
    var cs = window.getComputedStyle ? window.getComputedStyle(button) : null;
    if (cs && cs.position === 'static') button.style.position = 'relative';

    /* The theme zooms the label out while the request runs; bring it back
       at once so the flip starts from the resting label */
    var label = labelOf(button);
    if (label) {
      label.classList.remove('zoomOut');
      label.classList.remove('fadeInDown');
    }

    button.classList.remove('rr-ok');
    void button.offsetWidth;
    button.classList.add('rr-added');
    button.classList.add('rr-ok');
    announce(button.getAttribute('data-rr-added-label') || ADDED_LABEL);

    button.rrAddedTimer = window.setTimeout(function () {
      restoreLabel(button);
    }, ADDED_DURATION);
  }

  /* ------------------------------------------------------------------------
     (d2) Press "rev" (spec 5.2 v2.16, reference mobile option 1): on
     pointerdown every primary, cart, red and secondary button squashes
     (.rr-tap) while three chevrons shoot across (span.rr-shoot, injected
     once, not into <input> buttons or secondary ones). Never delays the
     click. Skipped under reduced motion.
     ------------------------------------------------------------------------ */
  var TAP_CHEVRON = [
    '.button.button--primary', 'a.button--primary.button', '.button.button--add-to-cart',
    'a.button--add-to-cart.button', '.action_button.add_to_cart', '.cart__checkout .checkout',
    '.gv-prot-cta .gv-prot-btn--protected', '.slideshow-slide__button--primary',
    '.slideshow-slide__button--button--primary', '.rr-card__add', '.rr-pair__add',
    '#gv-add-vehicle a.add-vehicle', '.lds-cta', '.bbq26-cta:not(.bbq26-cta--ghost):not(.bbq26-cta--secondary)',
    '.rr-button--primary', '.rr-button--red', '.rr-vtap'
  ].join(',');
  var TAP_PLAIN = [
    '.button.button--secondary', 'a.button--secondary.button', '.rr-button--secondary',
    '.slideshow-slide__button--button--secondary', '.slideshow-slide__button--secondary'
  ].join(',');

  function shootFor(button) {
    var s = button.querySelector(':scope > .rr-shoot');
    if (!s) {
      s = doc.createElement('span');
      s.className = 'rr-shoot';
      s.setAttribute('aria-hidden', 'true');
      s.innerHTML = '<i></i><i></i><i></i>';
      button.insertBefore(s, button.firstChild);
    }
    return s;
  }

  function onTapDown(event) {
    if (reduced()) return;
    if (event.button && event.button !== 0) return;
    var t = event.target;
    if (!t || !t.closest) return;
    var button = t.closest(TAP_CHEVRON);
    var chevrons = true;
    if (!button) {
      button = t.closest(TAP_PLAIN);
      chevrons = false;
    }
    if (!button || button.disabled || button.classList.contains('disabled')) return;
    if (chevrons && String(button.tagName).toUpperCase() !== 'INPUT') {
      try {
        shootFor(button);
        button.style.setProperty('--rr-shoot-dist', (button.offsetWidth + 30) + 'px');
      } catch (e) { /* the squash alone still plays */ }
    }
    button.classList.remove('rr-tap');
    void button.offsetWidth;
    button.classList.add('rr-tap');
  }

  function onTapEnd(event) {
    if (event.animationName === 'rr-squash' && event.target && event.target.classList) {
      event.target.classList.remove('rr-tap');
    }
  }

  doc.addEventListener('pointerdown', onTapDown, { passive: true, capture: true });
  doc.addEventListener('animationend', onTapEnd, true);

  /* ------------------------------------------------------------------------
     (d3) Product page main Add to cart: a one-time chevron nudge the first
     time it scrolls into view (spec 5.2 v2.16, reference mobile option 4).
     Product template only, once per page view, skipped under reduced motion.
     ------------------------------------------------------------------------ */
  function initNudge() {
    if (reduced() || !('IntersectionObserver' in window)) return;
    if (!doc.body || !/(^|\s)product(\s|$|-)/.test(doc.body.className)) return;
    var button = doc.querySelector('form.shopify-product-form .button--add-to-cart, form[action*="/cart/add"] .button--add-to-cart, form[action*="/cart/add"] .add_to_cart');
    if (!button || button.getAttribute('data-rr-nudged')) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.disconnect();
        button.setAttribute('data-rr-nudged', '1');
        window.setTimeout(function () {
          button.classList.add('rr-nudge');
          window.setTimeout(function () { button.classList.remove('rr-nudge'); }, 1900);
        }, 250);
      });
    }, { threshold: 0.9 });
    io.observe(button);
  }

  doc.addEventListener('gv:cart:added', onCartAdded);
  ready(initNudge);

  /* ------------------------------------------------------------------------
     (d4) Product page Add to cart plays the "✓ Added" flip too (owner,
     28 Sept 2026). The store's cart action is "redirect to cart", so the
     product form used to post straight to /cart and the page left before
     anything could show. Now the add goes through /cart/add.js with the
     form's own fields (variant, quantity, line item properties such as the
     ship-date acknowledgement), the flip plays, and (v2.18 6L E3) the
     shopper stays on the product page with an "Added to cart · View cart"
     note. Anything that already stopped the
     submit (the ship-date check, required options) is respected; if the add
     fails, the form is posted the normal way so Shopify's own message shows.
     Mini cart and drawer modes are left to the theme, which adds by AJAX.
     ------------------------------------------------------------------------ */
  var CART_REDIRECT_MS = 1100;

  function cartUrl() {
    try { return (window.PXUTheme && window.PXUTheme.routes && window.PXUTheme.routes.cart_url) || '/cart'; } catch (e) { return '/cart'; }
  }
  function themeCartAction() {
    try { return window.PXUTheme && window.PXUTheme.theme_settings && window.PXUTheme.theme_settings.cart_action; } catch (e) { return null; }
  }

  function refreshCartCount() {
    window.fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        setCount(productCount(cart));
        each(doc.querySelectorAll('.header-cart'), function (el) { el.classList.toggle('has-cart-count', cart.item_count > 0); });
      })
      .catch(function () { /* the count catches up on the next page */ });
  }

  function addedNote(button) {
    var host = button.closest('.purchase-details') || button.parentNode;
    if (!host || !host.parentNode) return;
    var note = doc.querySelector('.rr-added-note');
    if (!note) {
      note = doc.createElement('p');
      note.className = 'rr-added-note';
      note.setAttribute('role', 'status');
      note.appendChild(doc.createTextNode('Added to cart · '));
      var a = doc.createElement('a');
      a.href = cartUrl();
      a.textContent = 'View cart';
      note.appendChild(a);
      host.parentNode.insertBefore(note, host.nextSibling);
    }
    note.classList.remove('is-on');
    void note.offsetWidth;
    note.classList.add('is-on');
    window.clearTimeout(addedNote.t);
    addedNote.t = window.setTimeout(function () { note.classList.remove('is-on'); }, 4000);
  }

  function onProductSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
    if (event.defaultPrevented || form.getAttribute('data-rr-native')) return;
    var action = themeCartAction();
    if (action === 'mini_cart' || action === 'drawer') return;
    if (!window.fetch || !window.FormData) return;
    var button = form.querySelector('.button--add-to-cart, button[name="add"]');
    if (!button) return;
    event.preventDefault();
    if (button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    var data;
    try { data = new FormData(form); } catch (e) { form.setAttribute('data-rr-native', '1'); form.submit(); return; }
    window.fetch('/cart/add.js', {
      method: 'POST',
      body: data,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed');
      return r.json();
    }).then(function () {
      button.removeAttribute('aria-busy');
      doc.dispatchEvent(new CustomEvent('gv:cart:added', { detail: { button: button } }));
      /* v2.18 6L E3 (owner decision): stay on the product page. The count
         updates (and pops, (c) above) and a small note under the button
         reads "Added to cart · View cart" for 4 s. */
      refreshCartCount();
      addedNote(button);
    }).catch(function () {
      button.removeAttribute('aria-busy');
      form.setAttribute('data-rr-native', '1');
      form.submit();   /* the normal post: Shopify shows its own error */
    });
  }

  ready(function () {
    if (doc.body && /(^|\s)product(\s|$|-)/.test(doc.body.className)) {
      doc.addEventListener('submit', onProductSubmit, false);
    }
  });
  doc.addEventListener('rr:recs:added', function () { refreshCartCount(); });

  /* ------------------------------------------------------------------------
     (d5) Homepage slideshow (v2.18 6L C1, owner decision): slow autoplay is
     allowed here only. The theme's <section-slideshow> pauses while the
     pointer is over it and carries on when it leaves (owner, 28 Sept: hover
     is the pause control, no visible button), and stops for good once the
     arrows or dots are used; this adds the same pause on keyboard focus and
     no autoplay at all under reduced motion.
     ------------------------------------------------------------------------ */
  function initSlideshowControls() {
    /* C4: every slide link gets a name (the image links had none) */
    each(doc.querySelectorAll('section-slideshow a'), function (a) {
      if ((a.textContent || '').trim() || a.getAttribute('aria-label')) return;
      var slide = a.closest('.swiper-slide, [class*="slide"]');
      var h = slide && slide.querySelector('.slideshow-slide__heading, h2, h3');
      var name = h ? (h.textContent || '').trim() : '';
      a.setAttribute('aria-label', name ? 'Shop ' + name : 'View this offer');
    });
    each(doc.querySelectorAll('section-slideshow'), function (el) {
      if (el.getAttribute('data-rr-ctl')) return;
      if (typeof el._stopCustomAutoplay !== 'function') return;
      el.setAttribute('data-rr-ctl', '1');
      if (reduced()) {
        try { el._stopCustomAutoplay(); el.userInteracted = true; el.setAttribute('data-autoplay-active', 'false'); } catch (e) { /* theme API changed */ }
        return;
      }
      if (!el.autoplayActive && el.getAttribute('data-autoplay') !== 'true') return;
      var wrap = el.slideshowWrapper || el;
      wrap.addEventListener('focusin', function () {
        if (el.autoplayActive) { try { el._pauseCustomAutoplay(); } catch (e) { /* noop */ } }
      });
      wrap.addEventListener('focusout', function (ev) {
        if (ev.relatedTarget && wrap.contains(ev.relatedTarget)) return;
        if (el.autoplayActive && el.getAttribute('data-hover') !== 'true') { try { el._resumeCustomAutoplay(); } catch (e) { /* noop */ } }
      });
    });
  }
  ready(function () { setTimeout(initSlideshowControls, 600); });

  /* v2.18 6L B6: a collection tile is one tab stop (its image link and its
     title link go to the same place; the image link leaves the tab order) */
  ready(function () {
    each(doc.querySelectorAll('.collection-list__thumbnail, .rr-tile, .collection__thumbnail'), function (tile) {
      var seen = {};
      each(tile.querySelectorAll('a[href]'), function (a) {
        var h = a.getAttribute('href');
        if (seen[h]) a.setAttribute('tabindex', '-1');
        else seen[h] = true;
      });
    });
  });

  /* v2.18 6L B5: a long single-level dropdown (Categories, 21 links) shows
     every link in columns instead of a scroll box */
  ready(function () {
    each(doc.querySelectorAll('#header .navbar-dropdown.is-vertical'), function (dd) {
      var links = 0;
      each(dd.children, function (c) { if (c.tagName === 'A') links++; });
      if (links > 12 && !dd.querySelector('.navbar-dropdown')) dd.classList.add('rr-dd-cols');
    });
  });

  /* v7.1 (owner, 3 Oct 2026): Featured Vehicles looks like Categories. A
     dropdown of make submenus becomes a two-pane panel: the makes down the
     left in the Categories type, the models of the make you point at (or
     focus, or tap) on the right in three columns, so every model of the
     biggest make (Ford, 22) shows without scrolling. The no-JS checkboxes
     stay in the markup; here a make is a button that switches the pane.
     A short pause before switching lets the pointer cross other makes on
     its way to the models. --rr-i / --rr-j stagger the roll-out in
     rr-brand.css (transform and opacity only). */
  ready(function () {
    each(doc.querySelectorAll('#header .navbar-dropdown.is-vertical'), function (dd) {
      var subs = [];
      each(dd.children, function (c) { if (c.classList && c.classList.contains('has-submenu')) subs.push(c); });
      if (subs.length < 6) return;
      dd.classList.add('rr-dd-split');
      dd.style.setProperty('--rr-rows', subs.length);
      var timer = null;
      function show(sub) {
        clearTimeout(timer);
        each(subs, function (s) {
          var on = s === sub;
          s.classList.toggle('is-active', on);
          var lb = s.querySelector('label');
          if (lb) lb.setAttribute('aria-expanded', on ? 'true' : 'false');
        });
      }
      each(subs, function (sub, i) {
        var lb = sub.querySelector('label');
        var ul = sub.querySelector('.navbar-submenu');
        if (!lb || !ul) return;
        var name = (lb.textContent || '').trim();
        lb.style.setProperty('--rr-i', i);
        lb.setAttribute('tabindex', '0');
        lb.setAttribute('role', 'button');
        if (!ul.id) ul.id = 'rr-dd-pane-' + i;
        lb.setAttribute('aria-controls', ul.id);
        ul.setAttribute('data-rr-make', name);
        ul.setAttribute('aria-label', name);
        each(ul.querySelectorAll('li'), function (li, j) { li.style.setProperty('--rr-j', j); });
        lb.addEventListener('mouseenter', function () {
          clearTimeout(timer);
          timer = setTimeout(function () { show(sub); }, 90);
        });
        lb.addEventListener('mouseleave', function () { clearTimeout(timer); });
        lb.addEventListener('focus', function () { show(sub); });
        lb.addEventListener('click', function (e) { e.preventDefault(); show(sub); });
        lb.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
            e.preventDefault();
            show(sub);
            var a = ul.querySelector('a');
            if (a) a.focus();
          }
        });
        ul.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowLeft') { e.preventDefault(); lb.focus(); }
        });
      });
      show(subs[0]);
    });
    each(doc.querySelectorAll('#header .navbar-dropdown.rr-dd-cols'), function (dd) {
      var i = 0;
      each(dd.children, function (c) { if (c.tagName === 'A') c.style.setProperty('--rr-i', i++); });
    });
    /* Keep the wide panels on screen on narrow laptops (1024px: Featured
       Vehicles ran 9px past the edge). A margin, not a transform, so the
       roll-out animation is untouched. */
    each(doc.querySelectorAll('#header .navbar-dropdown.rr-dd-split, #header .navbar-dropdown.rr-dd-cols'), function (dd) {
      var item = dd.parentElement;
      function fit() {
        dd.style.marginLeft = '';
        var r = dd.getBoundingClientRect();
        if (!r.width) return;
        var over = r.right - (window.innerWidth - 12);
        if (over > 0) dd.style.marginLeft = (-Math.min(over, Math.max(0, r.left - 12))) + 'px';
      }
      function later() { if (window.requestAnimationFrame) window.requestAnimationFrame(fit); else fit(); }
      item.addEventListener('mouseenter', later);
      item.addEventListener('focusin', later);
      item.addEventListener('click', later);
    });
  });

  /* ------------------------------------------------------------------------
     (e) Sticky header
     Two jobs, both progressive. Without this file the header still sticks
     (snippets/scroll-smoothing.liquid pins .header--css-sticky at top: 0)
     and the logo simply keeps its rest width.

     1. --rr-announcement-h on <html>: the announcement bar's current height,
        0px when the bar is absent, closed, or not set to stick. The theme's
        own script floats the stuck bar over the top of the viewport, where
        it covered the top 46px of the stuck header and clipped the logo;
        rr-brand.css offsets the header by this value instead. Re-measured
        on load, on resize (debounced), on a cheap check during scroll, and
        whenever the bar's section mutates (close icon, sticky wrapper).
     2. rr-stuck on the header section once the page has scrolled past the
        header's own rest height. rr-brand.css reads it to bring the logo
        down to the section's scroll width, since the theme's is-sticky hook
        never lands on this header layout. The class toggles under reduced
        motion too; only the CSS transitions are switched off there.
     ------------------------------------------------------------------------ */
  var ANNOUNCEMENT_VAR = '--rr-announcement-h';
  var HEADER_SELECTOR = '.header-section.header--css-sticky';
  var RESIZE_DELAY = 150;

  var headerBound = false;
  var headerEl = null;
  var headerRestHeight = 0;
  var announcementHeight = -1;
  var announcementObserver = null;
  var announcementRoot = null;
  var measureQueued = false;
  var scrollQueued = false;
  var resizeTimer = null;

  function raf(fn) {
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(fn);
    } else {
      window.setTimeout(fn, 16);
    }
  }

  function announcementBar() {
    return doc.querySelector('.announcement-bar');
  }

  function announcementSection(bar) {
    return closest(bar, '.shopify-section') || bar.parentElement;
  }

  /* The bar's section settings ride along in its data-section-data JSON. A
     bar that is not set to stick scrolls away with the page and must not
     push the header down. Cached on the node; assumes sticky when unsure. */
  function announcementSticks(bar) {
    if (typeof bar.rrSticks === 'boolean') return bar.rrSticks;
    var sticks = true;
    var section = announcementSection(bar);
    var data = section && section.querySelector('script[data-section-data]');
    if (data) {
      try {
        sticks = JSON.parse(data.textContent || '{}').enable_sticky !== false;
      } catch (err) {
        sticks = true;
      }
    }
    bar.rrSticks = sticks;
    return sticks;
  }

  function measureAnnouncement() {
    if (!doc.documentElement) return;
    var bar = announcementBar();
    var wrapper = doc.querySelector('#sticky-wrapper.announcement-sticky-wrapper');
    var height = 0;

    if (bar) {
      height = announcementSticks(bar) ? bar.offsetHeight : 0;
    } else if (wrapper) {
      height = wrapper.offsetHeight;
    }

    height = Math.max(0, Math.round(height));
    if (height === announcementHeight) return;
    announcementHeight = height;
    doc.documentElement.style.setProperty(ANNOUNCEMENT_VAR, height + 'px');
  }

  function scheduleMeasure() {
    if (measureQueued) return;
    measureQueued = true;
    raf(function () {
      measureQueued = false;
      measureAnnouncement();
    });
  }

  /* One observer on the bar's section: catches the close icon hiding or
     removing the bar, the theme wrapping it in #sticky-wrapper, and the
     is-sticky class landing on that wrapper. */
  function observeAnnouncement() {
    if (!('MutationObserver' in window)) return;
    var bar = announcementBar();
    var root = bar ? announcementSection(bar) : null;
    if (!root) return;
    if (root === announcementRoot && root.isConnected !== false) return;

    if (announcementObserver) announcementObserver.disconnect();
    announcementObserver = new MutationObserver(scheduleMeasure);
    announcementObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden']
    });
    announcementRoot = root;
  }

  /* z__jsHeader.js adds header--css-sticky after this file has booted, so
     the section is looked up lazily and again if the editor re-renders it. */
  function headerSection() {
    if (!headerEl || headerEl.isConnected === false) {
      headerEl = doc.querySelector(HEADER_SELECTOR);
    }
    return headerEl;
  }

  /* Rest height is read only while the header is not stuck, so the shrunken
     logo never lowers the threshold and the toggle cannot flicker. */
  function measureHeaderRest() {
    var header = headerSection();
    if (!header || header.classList.contains('rr-stuck')) return;
    headerRestHeight = header.offsetHeight;
  }

  /* v2.16 (28 Sept 2026): the header's compact state is owned by
     assets/rr-compact.js (spec 6D thresholds, 120px/100px in and 20px out,
     with the rest height held in the flow). rr-stuck simply mirrors
     html.rr-compact here so the two scripts never disagree. */
  function updateStuck() {
    var header = headerSection();
    if (!header) return;
    var on = doc.documentElement.classList.contains('rr-compact');
    if (on !== header.classList.contains('rr-stuck')) header.classList.toggle('rr-stuck', on);
  }

  function onHeaderScroll() {
    if (scrollQueued) return;
    scrollQueued = true;
    raf(function () {
      scrollQueued = false;
      updateStuck();
      measureAnnouncement(); /* one offsetHeight read; writes only on change */
    });
  }

  function onHeaderResize() {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      measureHeaderRest();
      measureAnnouncement();
      updateStuck();
    }, RESIZE_DELAY);
  }

  function onAnnouncementClose(event) {
    if (!closest(event.target, '.js-close-announcement')) return;
    scheduleMeasure();
    window.setTimeout(measureAnnouncement, 500); /* after any slide-up the theme runs */
  }

  function initHeader() {
    if (!doc.documentElement) return;

    measureAnnouncement();
    observeAnnouncement();
    measureHeaderRest();
    updateStuck();

    if (headerBound) return;
    headerBound = true;

    window.addEventListener('scroll', onHeaderScroll, { passive: true });
    window.addEventListener('resize', onHeaderResize);
    window.addEventListener('load', onHeaderResize, { once: true });
    doc.addEventListener('click', onAnnouncementClose);
  }

  /* ------------------------------------------------------------------------
     (f) Floating buttons on phones (owner, 30 Sept 2026)
     The Gorgias chat button (and Omnisend's floating widget) sat over product
     names and prices in the bottom corner. On phones they slide off to the
     right while the shopper scrolls down, and come back on scroll up, after
     a short pause, or at the foot of the page. Only a class on <html> flips
     (rr-float-tuck); the movement is a CSS transform (rr-brand.css 6e.2), and
     nothing is measured per frame beyond scrollY.
     ------------------------------------------------------------------------ */
  var floatInit = false;
  function initFloatTuck() {
    if (floatInit || !window.matchMedia) return;
    floatInit = true;
    var phone = window.matchMedia('(max-width: 798px)');
    var root = doc.documentElement;
    var lastY = window.pageYOffset || 0;
    var tucked = false;
    var queued = false;
    var idle = null;
    function set(on) {
      if (on === tucked) return;
      tucked = on;
      root.classList.toggle('rr-float-tuck', on);
    }
    function check() {
      queued = false;
      var y = window.pageYOffset || 0;
      var dy = y - lastY;
      if (Math.abs(dy) < 6) return;
      lastY = y;
      var atFoot = y + window.innerHeight >= doc.documentElement.scrollHeight - 120;
      set(phone.matches && dy > 0 && y > 240 && !atFoot);
    }
    window.addEventListener('scroll', function () {
      if (!queued) { queued = true; window.requestAnimationFrame(check); }
      clearTimeout(idle);
      idle = setTimeout(function () { set(false); }, 1200);
    }, { passive: true });
    var off = function () { if (!phone.matches) set(false); };
    if (phone.addEventListener) phone.addEventListener('change', off);
    else if (phone.addListener) phone.addListener(off);
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  ready(function () {
    initDividers();
    initCartBump();
    initSlideProgress();
    initCountFix();
    initHero();
    initHeader();
    initFloatTuck();
  });

  /* Theme editor re-renders sections; pick up new dividers, badges, heroes and
     a re-rendered header or announcement bar */
  doc.addEventListener('shopify:section:load', function (event) {
    var root = event && event.target;
    initDividers(root);
    initCartBump(root);
    initSlideProgress(root);
    initCountFix(root);
    initHero();
    initHeader();
  });
})();
